import { useCallback, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import ListAccountSkeleton from '#client/components/ListAccountSkeleton';
import PageTitle from '#client/components/PageTitle';
import { FAQ_PAYEES_URL } from '#client/constants/FAQs';
import { GET_COUNTER_PARTIES_CONNECT_TYPE, COUNTER_PARTY_STATUS } from '#client/constants/payees';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBankingPath from '#client/hooks/useBankingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCounterparty from '#client/hooks/useCounterparty';
import useModalContext from '#client/hooks/useModalContext';
import useSkeleton from '#client/hooks/useSkeleton';

import EmptyPayee from './EmptyPayee';
import { ButtonStyled, DialogContentStyled } from './PayeeContainer.styles';
import PayeeList from './PayeeList';

import useUpdatePayee from '../hooks/useUpdatePayee';

const { isWebView } = WebView;

export default function PayeeContainer({ handleClose = () => {} }) {
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const bankingPath = useBankingPath();
  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();
  const { updatePayeeStatus, loading: loadingUpdatePayee } = useUpdatePayee();
  const { openModal } = useModalContext();

  const { data, accounts, hasMore, loadingAccounts, isLoadingMore, loadMore, resetPagination } =
    useCounterparty(GET_COUNTER_PARTIES_CONNECT_TYPE.PAYEE, COUNTER_PARTY_STATUS.ALL);
  const [showSkeleton] = useSkeleton(
    !isFetchingBankingAccounts && !!bankingAccountId && !loadingAccounts && !!data
  );
  const showLoadMoreButton = !showSkeleton && hasMore;

  const trackAddPayeeClick = useButtonPressedCallback({
    elementName: 'add_payeebutton',
    elementText: 'Add Payee',
    appPageName: 'payees_screen'
  });

  const handleOpenAddPayeeModal = () => {
    trackAddPayeeClick();
    openModal({
      component: AddCounterPartyModal,
      mode: AddCounterPartyModal.MODAL_TYPES.ADD_PAYEE,
      entryPageForSendMoney: `${bankingPath}/payees`,
      refetch: resetPagination
    });
  };

  /**
   * handle archived or unarchive for Payee
   */
  const handleUpdatePayeeStatus = useCallback(
    async ({ id, name, status }) => {
      await updatePayeeStatus({ id, name, status, bankingAccountId, handleClose, resetPagination });
    },
    [updatePayeeStatus, bankingAccountId, handleClose, resetPagination]
  );

  const payeeListAfterSort = useMemo(
    () =>
      orderBy(accounts, ['status', 'recentlyCreatedPayment', 'createdAt'], ['asc', 'desc', 'desc']),
    [accounts]
  );

  return (
    <DialogContentStyled>
      <Stack
        direction="column"
        spacing="32px"
      >
        {!isWebView && (
          <PageTitle
            appPageName="payees_screen"
            title="Payees"
            to={FAQ_PAYEES_URL}
          />
        )}
        <Box
          alignSelf={isMobile ? 'auto' : 'end'}
          paddingRight={isMobile ? '0' : '5px'}
        >
          <ButtonStyled
            aria-label="add payee action"
            fullWidth
            icon={<AddIcon fontSize="medium" />}
            onClick={handleOpenAddPayeeModal}
            size="small"
            variant="contained"
          >
            Add Payee
          </ButtonStyled>
        </Box>
      </Stack>
      {showSkeleton ? (
        <Stack paddingTop="32px">
          <ListAccountSkeleton aria-label="Loading payees" />
        </Stack>
      ) : (
        <Stack
          direction="column"
          paddingTop="32px"
          spacing="32px"
        >
          {!!accounts && accounts.length === 0 ? (
            <EmptyPayee handleAddPayee={handleOpenAddPayeeModal} />
          ) : (
            <>
              <Text
                color="primary"
                size="xs"
                uppercase
                weight="medium"
              >
                Recents
              </Text>
              <PayeeList
                handleDeleteSuccess={resetPagination}
                handleUpdatePayeeStatus={handleUpdatePayeeStatus}
                loadingAccounts={loadingAccounts || loadingUpdatePayee}
                payees={payeeListAfterSort}
              />
              <Stack
                direction="row"
                justifyContent="center"
                py="20px"
                spacing="8px"
              >
                {showLoadMoreButton && (
                  <LoadingButton
                    aria-label="load more"
                    loading={isLoadingMore}
                    onClick={loadMore}
                    variant="outlined"
                  >
                    Load more
                  </LoadingButton>
                )}
              </Stack>
            </>
          )}
        </Stack>
      )}
    </DialogContentStyled>
  );
}

PayeeContainer.propTypes = {
  handleClose: PropTypes.func
};
